import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { getCurrencies } from '../api/itemApi';
import { useSpace } from './SpaceContext';

const CurrencyContext = createContext({
  currency: null,
  activeCurrency: null,
  currencies: [],
  currenciesLoading: true,
});

export function CurrencyProvider({ children }) {
  const { activeSpace, isAuthenticated, signInError } = useAuth();
  const spaceId = (activeSpace?._isAdmin || activeSpace?._isFinance) ? null : (activeSpace?.spaceId ?? null);
  const { space, spaceLoading } = useSpace();

  const [currencies, setCurrencies] = useState([]);
  const [currenciesLoading, setCurrenciesLoading] = useState(true);

  useEffect(() => {
    // isAuthenticated guards against a stale spaceId in localStorage outliving a dead
    // session. signInError covers a stale MSAL account keeping isAuthenticated true after exchange fails.
    if (!isAuthenticated || signInError || !spaceId) {
      setCurrencies([]);
      setCurrenciesLoading(false);
      return;
    }

    setCurrenciesLoading(true);
    getCurrencies()
      .then((data) => {
        const mapped = data.map((c) => ({
          id: c.id,
          code: c.ISO,
          name: c.Name,
          symbol: c.Symbol,
          delimiter: c.Delimiter,
          decimalPlaces: c.DecimalPlaces ?? 2,
        }));
        setCurrencies(mapped);
      })
      .catch(() => {})
      .finally(() => setCurrenciesLoading(false));
  }, [spaceId, isAuthenticated, signInError]); // re-runs automatically whenever the active space changes

  // Derived from the space API response — updates automatically when space changes.
  // effectiveCurrency falls back to the space's entity's currency when the space has no
  // currency of its own set (space.currency stays available separately for any UI that needs
  // to show/edit the space's own explicit override, distinct from what's actually in effect).
  const activeCurrency = useMemo(() => {
    if (!space?.effectiveCurrency) return null;
    return {
      id:            space.effectiveCurrency.id,
      code:          space.effectiveCurrency.iso,
      name:          space.effectiveCurrency.name,
      symbol:        space.effectiveCurrency.symbol,
      delimiter:     space.effectiveCurrency.delimiter,
      decimalPlaces: space.effectiveCurrency.decimalPlaces,
    };
  }, [space?.effectiveCurrency]);

  const currency = activeCurrency?.code ?? null;

  return (
    <CurrencyContext.Provider
      value={{
        currency,
        activeCurrency,
        currencies,
        currenciesLoading: currenciesLoading || spaceLoading,
      }}
    >
      {children}
    </CurrencyContext.Provider>
  );
}

export const useCurrency = () => useContext(CurrencyContext);
